import * as i0 from '@angular/core';
import { Injectable, Injector, Pipe, EventEmitter, Component, Input, Output, ViewEncapsulation, Directive, NgModule } from '@angular/core';
import { createNativeInstance, tx } from '@transifex/native';
import { ReplaySubject } from 'rxjs';
import * as i1 from '@angular/platform-browser';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';

/** Singleton Injection */
/**
 * Service which wraps the Transifex Native library for using
 * inside components
 */
class TranslationService {
    constructor() {
        // A dictionary with additional TX Native instances for translation
        // eslint-disable-next-line space-infix-ops
        this.additionalInstances = {};
        // A subject for managing locale changes
        this.localeChangedSubject = new ReplaySubject(0);
        // A subject for managing the lazy loading of translations
        this.translationsFetchedSubject = new ReplaySubject(0);
    }
    // Observables for detecting locale change
    get localeChanged() {
        return this.localeChangedSubject;
    }
    // Observables for detecting translations fetching
    get translationsFetched() {
        return this.translationsFetchedSubject;
    }
    /**
     * Initializes the translation service
     *
     * @param config
     * @param instanceAlias
     * @returns void
     */
    async init(config) {
        const instance = this.getInstance(config.instanceAlias || '');
        instance.init(config);
        await this.getLanguages();
        this.translationsFetchedSubject.next((instance.fetchedTags &&
            instance.fetchedTags[instance.currentLocale] || []).
            indexOf(config.filterTags) !== -1);
    }
    /**
     * Sets the current locale
     *
     * @param locale
     * @param instanceAlias
     * @returns void
     */
    async setCurrentLocale(locale, instanceAlias) {
        const instance = this.getInstance(instanceAlias || '');
        try {
            await instance.setCurrentLocale(locale);
        }
        catch (e) {
            // add a logger here
            instance.currentLocale = locale;
        }
        this.localeChangedSubject.next(locale);
        this.translationsFetchedSubject.next((instance.fetchedTags &&
            instance.fetchedTags[instance.currentLocale] || []).
            indexOf(instance.filterTags) !== -1);
    }
    /**
     * Gets the current locale
     *
     * @param instanceAlias
     * @returns string
     */
    getCurrentLocale(instanceAlias) {
        const instance = this.getInstance(instanceAlias || '');
        return instance.getCurrentLocale() || '';
    }
    /**
     * Gets the languages collection
     *
     * @param instanceAlias
     * @returns any
     */
    async getLanguages(instanceAlias) {
        const instance = this.getInstance(instanceAlias || '');
        return instance.getLanguages();
    }
    /**
     * Translate a string
     *
     * @param str
     * @param params
     * @param instanceAlias
     * @returns void
     */
    translate(str, params, instanceAlias) {
        const instance = this.getInstance(instanceAlias || '');
        return instance.translate(str, params);
    }
    /**
     * Adds a new TX Native instance for alternative resource translations
     *
     * @param config
     * @returns boolean
     */
    async addInstance(config) {
        if (!config.token || !config.alias) {
            return false;
        }
        try {
            if (this.additionalInstances[config.alias]) {
                return true;
            }
            const txInstance = createNativeInstance({
                token: config.token,
            });
            if (txInstance) {
                if (config.controlled) {
                    await tx.controllerOf(txInstance);
                }
                this.additionalInstances[config.alias] = txInstance;
                return true;
            }
            return false;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Retrieves the instance that matches the given alias or the default one
     * if the alias does not match
     *
     * @param instanceAlias
     * @returns TXNative instance
     */
    getInstance(instanceAlias) {
        let instance = tx; // Fallback to TX Native main instance
        try {
            if (instanceAlias && this.additionalInstances[instanceAlias]) {
                instance = this.additionalInstances[instanceAlias];
            }
        }
        catch (e) { }
        return instance;
    }
    /**
     * Retrieves the tagged translations from the given instance
     * or from the default one
     *
     * @param tags
     * @param instanceAlias
     * @returns Promise<any>
     */
    async fetchTranslations(tags, instanceAlias) {
        const instance = this.getInstance(instanceAlias);
        if (instance && instance.currentLocale) {
            await instance.fetchTranslations(instance.currentLocale, { filterTags: tags });
            this.translationsFetchedSubject.next((instance && instance.fetchedTags && instance.fetchedTags[instance.currentLocale] || []).
                indexOf(tags) !== -1);
        }
        else {
            this.translationsFetchedSubject.next(true);
        }
    }
}
TranslationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TranslationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TranslationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TranslationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TranslationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * Decorator for using transparently the translation service as a property
 */
const T = (str, params, instanceConfig) => (target, key) => {
    const injector = Injector.create({
        providers: [
            { provide: TranslationService, useClass: TranslationService },
        ],
    });
    const translationService = injector.get(TranslationService);
    Object.defineProperty(target, key, {
        configurable: false,
        get: () => {
            if (instanceConfig) {
                translationService.addInstance(instanceConfig);
            }
            return translationService.translate(str, { ...params }, instanceConfig && instanceConfig.alias || '');
        },
    });
};

/**
 * A pipe for sanitizing safely HTML
 */
class SafeHtmlPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(value) {
        return this.sanitizer.bypassSecurityTrustHtml(value);
    }
}
SafeHtmlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: SafeHtmlPipe, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
SafeHtmlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: SafeHtmlPipe, name: "safeHtml" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: SafeHtmlPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'safeHtml' }]
        }], ctorParameters: function () { return [{ type: i1.DomSanitizer }]; } });

/* eslint-disable @typescript-eslint/no-useless-constructor */
/**
 * A TX Native instance selector component
 *
 * @param {string} alias
 * @param {string} token
 * @param {boolean} controlled
 * @param {EventEmitter} instanceReady
 */
class TXInstanceComponent {
    /**
     * Constructor
     *
     * @param translationService
     */
    constructor(translationService) {
        this.translationService = translationService;
        this.controlled = true;
        this.instanceReady = new EventEmitter();
        this.instanceReadySubject = new ReplaySubject(0);
    }
    // Observables for detecting instance readiness
    get instanceIsReady() {
        return this.instanceReadySubject;
    }
    async ngOnInit() {
        if (!this.token || !this.alias) {
            this.instanceReady.emit(false);
            this.instanceReadySubject.next(false);
        }
        const instanceCreated = await this.translationService.addInstance({
            token: this.token,
            alias: this.alias,
            controlled: this.controlled,
        });
        this.instance = this.translationService.getInstance(this.alias);
        if (instanceCreated && this.instance) {
            this.instanceReady.emit(true);
            this.instanceReadySubject.next(true);
        }
        else {
            this.instanceReady.emit(false);
            this.instanceReadySubject.next(false);
        }
    }
}
TXInstanceComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TXInstanceComponent, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Component });
TXInstanceComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.3", type: TXInstanceComponent, selector: "tx-instance", inputs: { alias: "alias", token: "token", controlled: "controlled" }, outputs: { instanceReady: "instanceReady" }, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TXInstanceComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tx-instance',
                    template: `
    <ng-content></ng-content>
  `,
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; }, propDecorators: { alias: [{
                type: Input
            }], token: [{
                type: Input
            }], controlled: [{
                type: Input
            }], instanceReady: [{
                type: Output
            }] } });

class TranslatePipe {
    /**
     * Constructor
     */
    constructor(translationService, instance, ref) {
        this.translationService = translationService;
        this.instance = instance;
        this.ref = ref;
        this.translation = '';
        this.lastStr = '';
        this.lastParams = {};
        // Update translation on locale change
        this.onLocaleChange = this.translationService.localeChanged.subscribe(() => {
            this.updateTranslation(this.lastStr, this.lastParams);
        });
        // Update translation if new ones are fetched
        this.onTranslationsFetch = this.translationService.translationsFetched.subscribe(() => {
            this.updateTranslation(this.lastStr, this.lastParams);
        });
    }
    /**
     * Updates the translation in case of locale changed
     */
    updateTranslation(str, params) {
        this.translation = this.translationService.translate(str, params, this.instance && this.instance.alias
            ? this.instance.alias
            : '');
        this.lastStr = str;
        this.lastParams = params;
        this.ref.markForCheck();
    }
    /**
     * Transforms str into a translated string.
     */
    transform(str, translateParams) {
        // Only translate when there are changes
        if (str === this.lastStr && JSON.stringify(translateParams) === JSON.stringify(this.lastParams)) {
            return this.translation;
        }
        const params = translateParams ?? {};
        this.lastStr = str;
        this.lastParams = params;
        this.translation = this.translationService.translate(str, params, this.instance && this.instance.alias
            ? this.instance.alias
            : '');
        return this.translation;
    }
    /**
     * Component destroy/dispose
     */
    ngOnDestroy() {
        this.onLocaleChange.unsubscribe();
        this.onTranslationsFetch.unsubscribe();
    }
}
TranslatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TranslatePipe, deps: [{ token: TranslationService }, { token: TXInstanceComponent }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Pipe });
TranslatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TranslatePipe, name: "translate", pure: false });
TranslatePipe.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TranslatePipe });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TranslatePipe, decorators: [{
            type: Injectable
        }, {
            type: Pipe,
            args: [{
                    name: 'translate',
                    pure: false, // required to update the translation
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }, { type: TXInstanceComponent }, { type: i0.ChangeDetectorRef }]; } });

/**
 * A translation component
 *
 * @param {string} str
 * @param {string=} key
 * @param {string=} context
 * @param {string=} comment
 * @param {number=} charlimit
 * @param {string=} tags
 * @param {boolean=} escapeVars
 * @param {boolean=} inline
 * @param {boolean=} sanitize
 * @param {Object=} vars
 */
class TComponent {
    /**
     * Constructor
     *
     * @param translationService
     * @param instance
     */
    constructor(translationService, instance) {
        this.translationService = translationService;
        this.instance = instance;
        this.str = '';
        this.key = '';
        this.context = '';
        this.comment = '';
        this.charlimit = 0;
        this.tags = '';
        this.escapeVars = false;
        this.inline = false;
        this.sanitize = false;
        this.translatedStr = '';
        this.actualVars = {};
        this.onLocaleChange = this.localeChanged.subscribe(() => {
            this.translate();
        });
        this.onTranslationsFetch =
            this.translationService.translationsFetched.subscribe(() => {
                this.translate();
            });
    }
    get vars() {
        return this.actualVars;
    }
    set vars(v) {
        this.actualVars = { ...v };
    }
    get translateParams() {
        return {
            _key: this.key,
            _context: this.context,
            _comment: this.comment,
            _charlimit: this.charlimit,
            _tags: this.tags,
            _escapeVars: this.escapeVars,
            _inline: this.inline,
            sanitize: this.sanitize,
        };
    }
    // Observable for detecting locale changes
    get localeChanged() {
        return this.translationService.localeChanged;
    }
    /**
     * Component initialization
     */
    ngOnInit() {
        this.translate();
    }
    /**
     * Component destruction
     */
    ngOnDestroy() {
        if (typeof this.onLocaleChange !== 'undefined') {
            this.onLocaleChange.unsubscribe();
            this.onLocaleChange = undefined;
        }
        if (typeof this.onTranslationsFetch !== 'undefined') {
            this.onTranslationsFetch.unsubscribe();
            this.onTranslationsFetch = undefined;
        }
    }
    /**
     * Input parameters change detector
     *
     * @param changes
     */
    ngOnChanges(changes) {
        this.translate();
    }
    /**
     * Translate a string using the translation service
     */
    translate() {
        this.translatedStr = this.translationService.translate(this.str, { ...this.translateParams, ...this.vars }, this.instance.alias || '');
    }
}
TComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TComponent, deps: [{ token: TranslationService }, { token: TXInstanceComponent }], target: i0.ɵɵFactoryTarget.Component });
TComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.3", type: TComponent, selector: "T", inputs: { str: "str", key: "key", context: "context", comment: "comment", charlimit: "charlimit", tags: "tags", escapeVars: "escapeVars", inline: "inline", sanitize: "sanitize", vars: "vars" }, usesOnChanges: true, ngImport: i0, template: `
    <ng-container *ngIf="!translateParams.sanitize">{{ translatedStr }}</ng-container>
    <span *ngIf="translateParams.sanitize" [innerHTML]="translatedStr | safeHtml"></span>
  `, isInline: true, directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "safeHtml": SafeHtmlPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'T',
                    template: `
    <ng-container *ngIf="!translateParams.sanitize">{{ translatedStr }}</ng-container>
    <span *ngIf="translateParams.sanitize" [innerHTML]="translatedStr | safeHtml"></span>
  `,
                    styles: [],
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }, { type: TXInstanceComponent }]; }, propDecorators: { str: [{
                type: Input
            }], key: [{
                type: Input
            }], context: [{
                type: Input
            }], comment: [{
                type: Input
            }], charlimit: [{
                type: Input
            }], tags: [{
                type: Input
            }], escapeVars: [{
                type: Input
            }], inline: [{
                type: Input
            }], sanitize: [{
                type: Input
            }], vars: [{
                type: Input
            }] } });

/**
 * A translation component with escaped variables
 *
 * @param {string} str
 * @param {string=} key
 * @param {string=} context
 * @param {string=} comment
 * @param {number=} charlimit
 * @param {string=} tags
 * @param {boolean=} escapeVars
 * @param {boolean=} inline
 * @param {Object=} vars
 */
class UTComponent extends TComponent {
    /**
     * Constructor
     *
     * @param translationService
     */
    constructor(translationService, instance) {
        super(translationService, instance);
        this.instance = instance;
        this.escapeVars = true;
    }
}
UTComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: UTComponent, deps: [{ token: TranslationService }, { token: TXInstanceComponent }], target: i0.ɵɵFactoryTarget.Component });
UTComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.3", type: UTComponent, selector: "UT", usesInheritance: true, ngImport: i0, template: `
    <div *ngIf="!translateParams._inline" [innerHTML]="translatedStr | safeHtml"></div>
    <span *ngIf="translateParams._inline" [innerHTML]="translatedStr | safeHtml"></span>
  `, isInline: true, directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "safeHtml": SafeHtmlPipe }, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: UTComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'UT',
                    template: `
    <div *ngIf="!translateParams._inline" [innerHTML]="translatedStr | safeHtml"></div>
    <span *ngIf="translateParams._inline" [innerHTML]="translatedStr | safeHtml"></span>
  `,
                    styles: [],
                    encapsulation: ViewEncapsulation.None,
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }, { type: TXInstanceComponent }]; } });

/**
 * A language picker component with the available languages
 * already populated
 */
class LanguagePickerComponent {
    /**
     * Constructor
     *
     * @param translationService
     */
    constructor(translationService, instance) {
        this.translationService = translationService;
        this.instance = instance;
        this.className = '';
        this.localeChanged = new EventEmitter();
        this.languages = [];
        this.getLanguages.bind(this);
        // When there is an alternative instance, listen to its readiness
        // and retrieve the languages again
        this.instanceIsReadySubscription =
            this.instanceReady.subscribe(async (ready) => {
                if (ready) {
                    this.languages = [];
                    await this.getLanguages();
                }
            });
    }
    // The locale selected on the TX Native instance
    get currentLocale() {
        return this.getCurrentLocale();
    }
    // Current language picker TXNative instance
    get activeInstance() {
        return this.instance.alias || '';
    }
    // Observable for detecting instance readiness
    get instanceReady() {
        return this.instance.instanceIsReady;
    }
    ngOnInit() {
        // Do not retrieve languages in the initialization
        // if alternative instace found, will fetch languages
        // when the instance is ready using a subscription
        if (this.instance && this.instance.alias) {
            return;
        }
        this.getLanguages();
    }
    /**
     * Retrieves the available languages
     *
     * @param translationService
     */
    async getLanguages() {
        this.languages =
            await this.translationService.getLanguages(this.activeInstance);
    }
    /**
     * Handles language selection changes
     *
     * @param event
     */
    async onChange(event) {
        const locale = event.target.value;
        await this.translationService.setCurrentLocale(locale, this.activeInstance);
        this.localeChanged.emit(locale);
    }
    /**
     * Returns the current locale from the active instance
     *
     * @param translationService
     */
    getCurrentLocale() {
        return this.translationService.getCurrentLocale(this.activeInstance);
    }
    /**
     * Component destruction
     */
    ngOnDestroy() {
        this.instanceIsReadySubscription.unsubscribe();
    }
}
LanguagePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: LanguagePickerComponent, deps: [{ token: TranslationService }, { token: TXInstanceComponent }], target: i0.ɵɵFactoryTarget.Component });
LanguagePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.3", type: LanguagePickerComponent, selector: "tx-language-picker", inputs: { className: "className" }, outputs: { localeChanged: "localeChanged" }, ngImport: i0, template: `
    <select [class]="className" (change)="onChange($event)" class="tx-language-picker">
      <option *ngFor="let language of languages"
        [selected]="language.code === currentLocale"
        [value]="language.code">
      {{ language.localized_name || language.name }}
      </option>
    </select>
  `, isInline: true, directives: [{ type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: LanguagePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tx-language-picker',
                    template: `
    <select [class]="className" (change)="onChange($event)" class="tx-language-picker">
      <option *ngFor="let language of languages"
        [selected]="language.code === currentLocale"
        [value]="language.code">
      {{ language.localized_name || language.name }}
      </option>
    </select>
  `,
                    styles: [],
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }, { type: TXInstanceComponent }]; }, propDecorators: { className: [{
                type: Input
            }], localeChanged: [{
                type: Output
            }] } });

/**
 * Directive for fetching translations in batches
 *
 * @param {string=} tags
 * @param {string=} instanceAlias
 */
class LoadTranslationsDirective {
    /**
     * Constructor
     *
     * @param translationService
     */
    constructor(translationService) {
        this.translationService = translationService;
        this.tags = '';
        this.instanceAlias = '';
        this.onLocaleChange = this.translationService.localeChanged.subscribe(async (locale) => {
            await this.updateTranslation();
        });
    }
    /**
     * On changes detected retrieve again the translations
     */
    async ngOnChanges(changes) {
        await this.updateTranslation();
    }
    /**
     * Retrieve the translations tagged with given tags
     */
    async updateTranslation() {
        await this.translationService.fetchTranslations(this.tags, this.instanceAlias);
    }
    /**
     * Clean any existing subscription to change events
     */
    ngOnDestroy() {
        if (typeof this.onLocaleChange !== 'undefined') {
            this.onLocaleChange.unsubscribe();
            this.onLocaleChange = undefined;
        }
    }
}
LoadTranslationsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: LoadTranslationsDirective, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Directive });
LoadTranslationsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.3", type: LoadTranslationsDirective, selector: "[txLoadTranslations]", inputs: { tags: ["txLoadTranslations", "tags"], instanceAlias: "instanceAlias" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: LoadTranslationsDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[txLoadTranslations]',
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; }, propDecorators: { tags: [{
                type: Input,
                args: ['txLoadTranslations']
            }], instanceAlias: [{
                type: Input
            }] } });

class TxNativeModule {
    /**
     * Use this method in your root module to provide the TranslationService
     */
    static forRoot() {
        return {
            ngModule: TxNativeModule,
            providers: [
                TranslationService,
            ],
        };
    }
}
TxNativeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TxNativeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TxNativeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TxNativeModule, declarations: [TComponent,
        UTComponent,
        LanguagePickerComponent,
        SafeHtmlPipe,
        TranslatePipe,
        TXInstanceComponent,
        LoadTranslationsDirective], imports: [CommonModule], exports: [TComponent,
        UTComponent,
        LanguagePickerComponent,
        SafeHtmlPipe,
        TranslatePipe,
        TXInstanceComponent,
        LoadTranslationsDirective] });
TxNativeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TxNativeModule, providers: [
        TXInstanceComponent,
    ], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: TxNativeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        TComponent,
                        UTComponent,
                        LanguagePickerComponent,
                        SafeHtmlPipe,
                        TranslatePipe,
                        TXInstanceComponent,
                        LoadTranslationsDirective,
                    ],
                    imports: [CommonModule],
                    exports: [
                        TComponent,
                        UTComponent,
                        LanguagePickerComponent,
                        SafeHtmlPipe,
                        TranslatePipe,
                        TXInstanceComponent,
                        LoadTranslationsDirective,
                    ],
                    providers: [
                        TXInstanceComponent,
                    ],
                }]
        }] });

/*
 * Public API Surface of tx-native-angular-sdk
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LanguagePickerComponent, LoadTranslationsDirective, SafeHtmlPipe, T, TComponent, TXInstanceComponent, TranslatePipe, TranslationService, TxNativeModule, UTComponent };
